import { useEffect, useState } from 'react';
import { useVault } from '../../../core/lib/vault';
import { getAwareness } from '../lib/yjsCollab';

interface PeerEntry {
  clientId: number;
  id?: string;
  name: string;
  color: string;
}

/**
 * Reads the active doc's Yjs Awareness instance and exposes the list of
 * peers currently viewing/editing it (excluding ourselves). Re-fires on
 * every awareness 'change' event — debounced naturally by the Awareness
 * implementation. Returns an empty list when the doc isn't loaded.
 */
function usePeers(activeDocId: string | null | undefined): PeerEntry[] {
  const [peers, setPeers] = useState<PeerEntry[]>([]);

  useEffect(() => {
    if (!activeDocId) {
      setPeers([]);
      return;
    }
    const awareness = getAwareness(activeDocId);
    if (!awareness) {
      setPeers([]);
      return;
    }
    const refresh = () => {
      const list: PeerEntry[] = [];
      const myClientId = awareness.clientID;
      const states = awareness.getStates();
      for (const [clientId, raw] of states) {
        if (clientId === myClientId) continue;
        const u = (raw as any)?.user;
        if (!u || typeof u.name !== 'string' || typeof u.color !== 'string') continue;
        list.push({
          clientId,
          id: typeof u.id === 'string' ? u.id : undefined,
          name: u.name,
          color: u.color,
        });
      }
      // De-dupe by user id when present (one human can have laptop + phone open).
      const seen = new Set<string>();
      const unique = list.filter((p) => {
        const key = p.id ?? `client:${p.clientId}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setPeers(unique);
    };
    refresh();
    awareness.on('change', refresh);
    return () => awareness.off('change', refresh);
  }, [activeDocId]);

  return peers;
}

/**
 * Avatar bar shown in the editor title strip. Renders one coloured dot per
 * peer currently viewing the active doc (excluding ourselves), tooltip-ed
 * with their username. Hidden when no peers are present so the toolbar
 * stays uncluttered when working alone.
 */
export function PresenceBar({ activeDocId }: { activeDocId: string | null }) {
  const peers = usePeers(activeDocId);
  const me = useVault((s) => s.user);

  if (peers.length === 0) return null;

  // Render up to 4 dots inline; collapse the rest into a "+N" overflow.
  const visible = peers.slice(0, 4);
  const overflow = peers.length - visible.length;

  return (
    <div
      className="flex items-center gap-1"
      title={
        peers.length === 1 && peers[0]
          ? `${peers[0].name} is also here`
          : `${peers.length} others here: ${peers.map((p) => p.name).join(', ')}`
      }
      aria-label={`${peers.length} other user${peers.length === 1 ? '' : 's'} on this page`}
    >
      <div className="flex -space-x-1.5">
        {visible.map((p) => (
          <span
            key={p.clientId}
            className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-ink-900 text-[10px] font-semibold text-white shadow"
            style={{ backgroundColor: p.color }}
            aria-label={p.name}
          >
            {initialsOf(p.name)}
          </span>
        ))}
        {overflow > 0 && (
          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-ink-900 bg-ink-700 px-1 text-[10px] font-semibold text-ink-100 shadow">
            +{overflow}
          </span>
        )}
      </div>
      {/* Tiny "+ me" hint when the user is alone-with-others — helps confirm
          presence is wired correctly during testing. */}
      {me && (
        <span className="text-[10px] text-ink-500" aria-hidden>
          {peers.length === 1 ? 'is also here' : 'are also here'}
        </span>
      )}
    </div>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  if (parts.length === 1) return first.toUpperCase() || '?';
  const second = parts[1]?.[0] ?? '';
  return (first + second).toUpperCase() || '?';
}
